import CheckCircleOutlineRounded from '@mui/icons-material/CheckCircleOutlineRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { visuallyHidden } from '@mui/utils';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ToastProps } from './Toast.props';
import { TOAST_TYPE, toastAccent, toastCardSx, toastProgressSx, toastTones } from './Toast.styles';
import { ToastCopyButton } from './ToastCopyButton';
import { TOAST_EXIT_MS, ToastVariant, toastRole, toastVariantLabel } from '../../../utils/toast';

/** WCAG 2.1 AA target size — the toast is reachable on touch. */
const TOUCH_TARGET_PX = 44;

const ICONS: Record<ToastVariant, typeof InfoOutlined> = {
  success: CheckCircleOutlineRounded,
  error: ErrorOutlineRounded,
  warning: WarningAmberRounded,
  info: InfoOutlined,
};

export const Toast: React.FC<ToastProps> = ({
  toast,
  depth,
  offset,
  paused,
  expanded,
  placement,
  onMeasure,
  onDismiss,
  onRemove,
}) => {
  const theme = useTheme();
  const tones = toastTones(theme);
  const cardRef = useRef<HTMLDivElement | null>(null);
  // Held false for the first paint so the card has somewhere to animate in
  // from; a CSS enter animation would replay every time the stack reshuffles.
  const [isSettled, setIsSettled] = useState(false);
  const isExiting = toast.status === 'exiting';
  const Icon = ICONS[toast.variant];

  const { id, duration, sequence } = toast;
  // Time left on the clock. Survives pause/resume, and resets when a repeat
  // toast collapses onto this one.
  const remainingRef = useRef<number | null>(duration);

  // When this toast appeared, for the copied report. Refreshed when a repeat
  // collapses onto it, because that is the occurrence the user is looking at.
  // Adjusted during render rather than in an effect: the copy button reads it as
  // a prop, so an effect would hand it the *previous* occurrence on the render
  // that shows the new one.
  const [occurrence, setOccurrence] = useState(() => ({
    sequence,
    at: new Date().toISOString(),
  }));
  if (occurrence.sequence !== sequence) {
    setOccurrence({ sequence, at: new Date().toISOString() });
  }

  useEffect(() => {
    remainingRef.current = duration;
  }, [duration, sequence]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setIsSettled(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  // Report height so the stack can work out where each card sits. Measured
  // after paint, and again whenever the content changes.
  useEffect(() => {
    if (cardRef.current) onMeasure(id, cardRef.current.offsetHeight);
  }, [id, sequence, expanded, toast.title, toast.description, onMeasure]);

  useEffect(() => {
    if (isExiting) {
      const timer = setTimeout(() => onRemove(id), TOAST_EXIT_MS);
      return () => clearTimeout(timer);
    }
    if (duration === null || paused) return undefined;

    const remaining = remainingRef.current ?? duration;
    const startedAt = Date.now();
    const timer = setTimeout(() => onDismiss(id), remaining);
    return () => {
      clearTimeout(timer);
      remainingRef.current = Math.max(0, remaining - (Date.now() - startedAt));
    };
  }, [id, duration, sequence, paused, isExiting, onDismiss, onRemove]);

  const handleAction = useCallback(() => {
    toast.action?.onClick();
    onDismiss(id);
  }, [toast.action, onDismiss, id]);

  return (
    <Box
      ref={cardRef}
      // Each card is its own live region: errors interrupt, everything else
      // waits. No `aria-label` — that would replace the announcement with the
      // title alone and drop the description.
      role={toastRole(toast.variant)}
      data-testid={`toast-${id}`}
      sx={toastCardSx(toast.variant, { depth, offset, placement, expanded, isExiting, isSettled })}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.75, py: 1.5 }}>
        <Icon
          aria-hidden
          sx={{ fontSize: 20, flexShrink: 0, color: toastAccent(theme, toast.variant) }}
        />
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          {/* Sighted users get the outcome from the icon's shape and colour;
              this is the same information for everyone else. */}
          <Box component="span" sx={visuallyHidden}>
            {`${toastVariantLabel(toast.variant)}: `}
          </Box>
          <Typography sx={{ ...TOAST_TYPE.title, color: tones.title }}>{toast.title}</Typography>
          {toast.description && (
            <Typography
              title={toast.description}
              sx={{
                ...TOAST_TYPE.body,
                color: tones.body,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {toast.description}
            </Typography>
          )}
        </Box>
        {toast.action && (
          <Button
            onClick={handleAction}
            sx={{
              flexShrink: 0,
              // 44px is the touch-target floor; the toast is reachable on mobile
              // and the action is often the only reason it is on screen.
              minWidth: TOUCH_TARGET_PX,
              minHeight: TOUCH_TARGET_PX,
              ...TOAST_TYPE.action,
              textTransform: 'none',
              color: toastAccent(theme, toast.variant),
              '&:hover': { backgroundColor: tones.hoverOverlay },
            }}
          >
            {toast.action.label}
          </Button>
        )}
        {toast.copyable && (
          <ToastCopyButton toast={toast} occurredAt={occurrence.at} size={TOUCH_TARGET_PX} />
        )}
        {duration === null && (
          <IconButton
            aria-label={`Dismiss: ${toast.title}`}
            onClick={() => onDismiss(id)}
            sx={{
              flexShrink: 0,
              width: TOUCH_TARGET_PX,
              height: TOUCH_TARGET_PX,
              color: tones.body,
              '&:hover': { color: tones.title, backgroundColor: tones.hoverOverlay },
            }}
          >
            <CloseRounded sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>
      {duration !== null && (
        // Keyed on the collapse count so a repeat toast restarts the drain.
        <Box key={sequence} sx={toastProgressSx(toast.variant, { duration, paused })} />
      )}
    </Box>
  );
};
